/**
 * How the laptop's capture stream is ASSEMBLED — the two decisions in
 * `useCamera` that are silent when they go wrong, pulled out here so they can
 * be tested without a browser.
 *
 * Both are about the same failure: the exam looks fine and the recording is
 * not. One loses the video to a microphone nobody needed; the other loses the
 * sound while every indicator says it is being captured.
 */

export interface TierOptions {
  facingMode: "user" | "environment";
  width: number;
  height: number;
  /** Whether the microphone is wanted at all. */
  audio: boolean;
}

/**
 * The constraint sets to try, in order, until one succeeds.
 *
 * **The last tier never asks for audio.** `getUserMedia` is atomic: ask for
 * `{ video, audio: true }` on a machine where the microphone is blocked and
 * the WHOLE call rejects with NotAllowedError — the camera is refused too,
 * even though the camera itself is allowed. So audio is requested first and
 * then dropped, which is what makes the microphone a bonus rather than a gate
 * on the recording that actually matters.
 *
 * The video constraints relax the same way, for cheap hardware that cannot
 * satisfy the size and frame-rate hints and would otherwise fail outright with
 * OverconstrainedError.
 */
export function mediaTiers(options: TierOptions): MediaStreamConstraints[] {
  const { facingMode, width, height, audio } = options;

  const videoTiers: MediaTrackConstraints[] = [
    {
      facingMode,
      width: { ideal: width },
      height: { ideal: height },
      // Low-data mode: 640x480 @ ~15fps keeps a face recognizable while letting
      // the encoder hit a low bitrate cleanly (matters on mobile data).
      frameRate: { ideal: 15, max: 20 },
    },
    { facingMode },
  ];

  const tiers: MediaStreamConstraints[] = [];
  if (audio) {
    for (const v of videoTiers) tiers.push({ video: v, audio: true });
    tiers.push({ video: true, audio: true });
  }
  for (const v of videoTiers) tiers.push({ video: v, audio: false });
  tiers.push({ video: true, audio: false });
  return tiers;
}

/**
 * Puts a freshly granted microphone track onto the live capture.
 *
 * **Returns a NEW MediaStream — it does not `addTrack` onto the old one.** A
 * MediaRecorder ignores tracks added to a stream it is already recording: the
 * track would be live, a level meter would move, the UI would say the
 * microphone was on, and the recording would still be silent. A new stream
 * object is what makes the owner rebuild the recorder, and only the rebuilt
 * recorder encodes the audio.
 *
 * The video track is CARRIED OVER rather than re-acquired, so the picture never
 * blinks and the camera is not asked for a second time.
 *
 * Any audio track already on the stream is STOPPED, not merely dropped: a dead
 * or superseded track that is left running holds the microphone open — one more
 * per retry — and the browser goes on showing the recording indicator after the
 * exam has ended.
 */
export function attachMic(
  current: MediaStream,
  track: MediaStreamTrack,
): MediaStream {
  const video = current.getVideoTracks();
  for (const old of current.getAudioTracks()) old.stop();
  return new MediaStream([...video, track]);
}

/** Whether a stream is carrying a microphone that is actually still running. */
export function hasLiveAudio(stream: MediaStream | null): boolean {
  return !!stream?.getAudioTracks().some((t) => t.readyState === "live");
}
